import { Fragment, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, TrendingDown, TrendingUp } from 'lucide-react';
import { requisitarAtivacao } from '@/hooks/useAtivacaoLinhas';
import type { Segmento } from '../triagem/segmentoTriagem';
import { TRIAGEM_TH, TRIAGEM_TD, TRIAGEM_TR_BASE, TRIAGEM_TR_LIVRE, TRIAGEM_SELO_BASE, TRIAGEM_SELO_OK, TRIAGEM_SELO_PENDENTE, TRIAGEM_SELO_REPROVADO } from '../triagem/triagemTabela';

export interface Indicador {
  pedidos: number; com_nota: number; media: number | null; taxa: number | null; notas: number[];
  media_anterior?: number | null; taxa_anterior?: number | null; variacao?: number | null;
}
export interface PedidoNota { pedido_id: string; numero_pedido: string; razao_social: string; documento?: string | null; nota: number | null; nota_em: string | null; concluido: boolean; bv_estado: string | null }
export interface VisaoNotas {
  competencia: string | null; competencia_anterior: string | null; inicio: string; competencias: string[];
  meses: Array<Indicador & { competencia: string }>;
  geral: Indicador;
  equipes: Array<Indicador & { equipe_id: string; equipe: string }>;
  consultores: Array<Indicador & { consultor_id: string; consultor: string; foto_url?: string | null; equipe: string | null; equipe_id?: string | null; pedidos_lista: PedidoNota[] }>;
}

const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const mes = (c: string | null) => c ? `${MESES[Number(c.slice(5, 7)) - 1]}/${c.slice(2, 4)}` : '—';
const media = (v: number | null | undefined) => v == null ? '—' : v.toFixed(2).replace('.', ',');
const pct = (v: number | null | undefined) => v == null ? '—' : `${String(v).replace('.', ',')}%`;
// 4 e 5 é desfecho bom; 3 é dívida; 1 e 2 é falha de verdade.
const corNota = (n: number | null) => n == null ? TRIAGEM_SELO_PENDENTE : n >= 4 ? TRIAGEM_SELO_OK : n === 3 ? TRIAGEM_SELO_PENDENTE : TRIAGEM_SELO_REPROVADO;

function Variacao({ v, anterior }: { v?: number | null; anterior?: number | null }) {
  if (v == null) return <span className="text-[10px] text-muted-foreground">{anterior == null ? 'sem mês anterior' : '—'}</span>;
  if (v === 0) return <span className="text-[10px] text-muted-foreground">igual</span>;
  const sobe = v > 0;
  const Icone = sobe ? TrendingUp : TrendingDown;
  return <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold ${sobe ? TRIAGEM_SELO_OK : TRIAGEM_SELO_REPROVADO}`}>
    <Icone className="h-3 w-3" aria-hidden="true" />{sobe ? '+' : ''}{media(v)}</span>;
}

function Tile({ titulo, valor, sub }: { titulo: string; valor: string; sub?: ReactNode }) {
  return <div className="rounded-lg border border-border bg-card px-3 py-2">
    <span className="block text-[10px] uppercase tracking-wide text-muted-foreground">{titulo}</span>
    <span className="block font-display text-[20px] font-bold tabular-nums text-foreground">{valor}</span>
    {sub && <span className="block text-[10px] text-muted-foreground">{sub}</span>}
  </div>;
}

/** A mesma consulta da triagem e da aba da Visão Prime — um cache só. */
export function useNotasBoasVindas(segmento: Segmento, competencia?: string, ativo = true) {
  return useQuery({ queryKey: ['bv-notas', segmento, competencia], enabled: ativo,
    queryFn: () => requisitarAtivacao<VisaoNotas>(`boas-vindas/notas?segmento=${segmento}${competencia ? `&competencia=${competencia}` : ''}`),
    staleTime: 60_000 });
}

/**
 * VISÃO DAS NOTAS DE BOAS-VINDAS (Douglas 24/09/2026). A nota que o cliente
 * dá ao fechar a Boas-vindas é a nota do atendimento inteiro, da venda à
 * ativação: aqui ela vira a nota do consultor, da equipe e da casa, mês a mês.
 * Só nota de cliente entra — o 5 administrativo do passado não suja a média.
 */
export function VisaoNotasBoasVindas({ segmento, competenciaInicial }: { segmento: Segmento; competenciaInicial?: string }) {
  const [competencia, setCompetencia] = useState<string | undefined>(competenciaInicial);
  const [aberto, setAberto] = useState<string | null>(null);
  const consulta = useNotasBoasVindas(segmento, competencia);
  const d = consulta.data;
  if (consulta.isLoading) return <p className="p-3 text-xs text-muted-foreground">Carregando notas…</p>;
  if (consulta.error) return <p role="alert" className="p-3 text-xs text-destructive">{consulta.error.message}</p>;
  if (!d?.competencia) return <p className="p-3 text-xs text-muted-foreground">Ainda não há pedidos na régua de notas deste segmento.</p>;
  const g = d.geral;
  return <div className="space-y-3 p-3">
    <div className="flex flex-wrap items-center gap-1.5">
      {d.competencias.map(c => <button key={c} type="button" onClick={() => { setCompetencia(c); setAberto(null); }}
        className={`${TRIAGEM_SELO_BASE} ${c === d.competencia ? 'border-primary/60 bg-primary/10 text-primary' : 'text-muted-foreground hover:text-primary'}`}>{mes(c)}</button>)}
      <span className="ml-auto text-[10px] text-muted-foreground">Só notas dadas pelo cliente, desde {mes(d.inicio)} · comparação com {mes(d.competencia_anterior)}</span>
    </div>

    <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
      <Tile titulo="Nota da casa" valor={media(g.media)} sub={<Variacao v={g.variacao} anterior={g.media_anterior} />} />
      <Tile titulo="Pedidos do mês" valor={String(g.pedidos)} sub="na Boas-vindas" />
      <Tile titulo="Com nota" valor={String(g.com_nota)} sub={`${pct(g.taxa)} responderam · antes ${pct(g.taxa_anterior)}`} />
      <div className="rounded-lg border border-border bg-card px-3 py-2">
        <span className="block text-[10px] uppercase tracking-wide text-muted-foreground">Distribuição</span>
        <div className="mt-1 space-y-0.5">{[5, 4, 3, 2, 1].map(n => {
          const qtd = g.notas[n - 1];
          const largura = g.com_nota ? Math.round((qtd / g.com_nota) * 100) : 0;
          return <div key={n} className="flex items-center gap-1.5 text-[10px]">
            <span className={`w-2 font-semibold ${corNota(n)}`}>{n}</span>
            <span className="h-1.5 flex-1 overflow-hidden rounded bg-muted"><span className="block h-full rounded bg-primary/70" style={{ width: `${largura}%` }} /></span>
            <span className="w-5 text-right tabular-nums text-muted-foreground">{qtd}</span>
          </div>;
        })}</div>
      </div>
    </div>

    <div className="grid gap-3 lg:grid-cols-2">
      <table className="w-full table-fixed"><thead><tr>
        <th className={TRIAGEM_TH}>Mês a mês</th><th className={`${TRIAGEM_TH} w-[70px]`}>Pedidos</th>
        <th className={`${TRIAGEM_TH} w-[70px]`}>Com nota</th><th className={`${TRIAGEM_TH} w-[70px]`}>Taxa</th><th className={`${TRIAGEM_TH} w-[70px]`}>Média</th>
      </tr></thead><tbody>{d.meses.map(m => <tr key={m.competencia} className={`${TRIAGEM_TR_BASE} ${m.competencia === d.competencia ? 'bg-primary/5' : ''}`}>
        <td className={`${TRIAGEM_TD} text-[11px] font-semibold`}>{mes(m.competencia)}</td>
        <td className={`${TRIAGEM_TD} text-[11px] tabular-nums`}>{m.pedidos}</td>
        <td className={`${TRIAGEM_TD} text-[11px] tabular-nums`}>{m.com_nota}</td>
        <td className={`${TRIAGEM_TD} text-[11px] tabular-nums`}>{pct(m.taxa)}</td>
        <td className={`${TRIAGEM_TD} text-[11px] font-semibold tabular-nums`}>{media(m.media)}</td>
      </tr>)}</tbody></table>

      <table className="w-full table-fixed"><thead><tr>
        <th className={TRIAGEM_TH}>Equipe · {mes(d.competencia)}</th><th className={`${TRIAGEM_TH} w-[60px]`}>Pedidos</th>
        <th className={`${TRIAGEM_TH} w-[62px]`}>Taxa</th><th className={`${TRIAGEM_TH} w-[56px]`}>Média</th><th className={`${TRIAGEM_TH} w-[74px]`}>vs anterior</th>
      </tr></thead><tbody>{d.equipes.map(e => <tr key={e.equipe_id} className={`${TRIAGEM_TR_BASE} ${TRIAGEM_TR_LIVRE}`}>
        <td className={`${TRIAGEM_TD} truncate text-[11px] font-semibold`}>{e.equipe}</td>
        <td className={`${TRIAGEM_TD} text-[11px] tabular-nums`}>{e.pedidos}</td>
        <td className={`${TRIAGEM_TD} text-[11px] tabular-nums`}>{pct(e.taxa)}</td>
        <td className={`${TRIAGEM_TD} text-[11px] font-semibold tabular-nums`}>{media(e.media)}</td>
        <td className={TRIAGEM_TD}><Variacao v={e.variacao} anterior={e.media_anterior} /></td>
      </tr>)}</tbody></table>
    </div>

    <table className="w-full table-fixed"><thead><tr>
      <th className={TRIAGEM_TH}>Consultor · pedidos de {mes(d.competencia)}</th><th className={`${TRIAGEM_TH} w-[120px]`}>Equipe</th>
      <th className={`${TRIAGEM_TH} w-[64px]`}>Pedidos</th><th className={`${TRIAGEM_TH} w-[70px]`}>Com nota</th>
      <th className={`${TRIAGEM_TH} w-[64px]`}>Média</th><th className={`${TRIAGEM_TH} w-[74px]`}>Anterior</th><th className={`${TRIAGEM_TH} w-[78px]`}>Variação</th>
    </tr></thead><tbody>{d.consultores.map(c => {
      const Seta = aberto === c.consultor_id ? ChevronDown : ChevronRight;
      return <Fragment key={c.consultor_id}>
        <tr className={`${TRIAGEM_TR_BASE} ${TRIAGEM_TR_LIVRE} cursor-pointer`} onClick={() => setAberto(a => a === c.consultor_id ? null : c.consultor_id)}>
          <td className={TRIAGEM_TD}><span className="flex items-center gap-1 truncate text-[11px] font-semibold"><Seta className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden="true" />{c.consultor}</span></td>
          <td className={`${TRIAGEM_TD} truncate text-[10px] text-muted-foreground`}>{c.equipe || '—'}</td>
          <td className={`${TRIAGEM_TD} text-[11px] tabular-nums`}>{c.pedidos}</td>
          <td className={`${TRIAGEM_TD} text-[11px] tabular-nums`}>{c.com_nota} <span className="text-[9px] text-muted-foreground">({pct(c.taxa)})</span></td>
          <td className={`${TRIAGEM_TD} text-[11px] font-semibold tabular-nums`}>{media(c.media)}</td>
          <td className={`${TRIAGEM_TD} text-[11px] tabular-nums text-muted-foreground`}>{media(c.media_anterior)}</td>
          <td className={TRIAGEM_TD}><Variacao v={c.variacao} anterior={c.media_anterior} /></td>
        </tr>
        {aberto === c.consultor_id && <tr><td colSpan={7} className="bg-muted/30 px-6 py-1.5">
          <div className="grid gap-x-4 gap-y-0.5 md:grid-cols-2">{c.pedidos_lista.map(p => <div key={p.pedido_id} className="flex items-center gap-2 text-[10px]">
            <span className={`${TRIAGEM_SELO_BASE} ${corNota(p.nota)} w-[52px] justify-center`}>{p.nota != null ? `nota ${p.nota}` : 'sem nota'}</span>
            <span className="truncate font-medium" title={p.razao_social}>{p.razao_social}</span>
            <span className="ml-auto shrink-0 tabular-nums text-muted-foreground">{p.numero_pedido}</span>
          </div>)}</div>
        </td></tr>}
      </Fragment>;
    })}</tbody></table>
  </div>;
}
